import { useState, useEffect } from "react";

import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Layout,
  LogIn,
  LogOut,
  Menu,
  Moon,
  Rss,
  Sun,
  UserCircle2,
} from "lucide-react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import gryllIcon from "@/assets/gryll-icon.png";

import { useTheme } from "../context/ThemeContext";
import NotificationBell from "./notifications/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const Sidebar = ({
  handleLogin,
  handleLogout,
  user = null,
  notifications = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", collapsed);
  }, [collapsed]);

  // ============================================
  // NAV ITEMS CONFIG
  // ============================================
  const navItems = [
    { path: "/", icon: Layout, label: "Builder" },
    { path: "/feed", icon: Rss, label: "Feed" },
    { path: "/library", icon: Bookmark, label: "Library" },
  ];

  const renderProfileIcon = (size = 16) => {
    if (!user) {
      return <UserCircle2 size={size} />;
    }

    const avatarUrl = user.avatar_url || user.avatarUrl || "";
    const initials =
      user.displayName?.trim()?.[0]?.toUpperCase() ||
      user.email?.trim()?.[0]?.toUpperCase() ||
      "U";

    return (
      <Avatar
        className={
          size >= 18
            ? "h-5 w-5 border border-border"
            : "h-4 w-4 border border-border"
        }
      >
        <AvatarImage src={avatarUrl || undefined} alt="Profile avatar" />
        <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
      </Avatar>
    );
  };

  // ============================================
  // NAV ITEM COMPONENT
  // ============================================
  const NavItem = ({ path, icon: Icon, label, accent }) => {
    const isStudioPath = (pathname) =>
      pathname === "/studio" || pathname === "/library";
    const isMarketPath = (pathname) => pathname === "/feed";
    const active =
      path === "/library"
        ? isStudioPath(location.pathname)
        : path === "/market"
          ? isMarketPath(location.pathname)
          : location.pathname === path;

    const variant = active ? "default" : "ghost";
    const className = [
      "w-full justify-start gap-3 h-9 text-xs font-medium",
      collapsed ? "px-2 justify-center" : "px-3",
      active && accent
        ? "bg-[var(--sidebar-active)] hover:bg-[var(--sidebar-active)]/90 text-[var(--sidebar-active-foreground)] shadow-md shadow-[var(--sidebar-active)]/20"
        : active
          ? "bg-[var(--sidebar-active)] hover:bg-[var(--sidebar-active)]/90 text-[var(--sidebar-active-foreground)] shadow-md"
          : accent
            ? "text-[var(--sidebar-active)] dark:text-[var(--sidebar-active)] hover:bg-[var(--sidebar-hover)]"
            : "text-muted-foreground hover:bg-[var(--sidebar-hover)] hover:text-foreground",
    ]
      .filter(Boolean)
      .join(" ");

    const buttonContent = (
      <Button
        variant={variant}
        size={collapsed ? "icon-sm" : "sm"}
        className={className}
        onClick={() => {
          navigate(path);
          setIsDrawerOpen(false);
        }}
      >
        <Icon size={18} className="flex-shrink-0" />
        {!collapsed && <span className="whitespace-nowrap">{label}</span>}
      </Button>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent side="right">
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return buttonContent;
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <TooltipProvider>
      {/* MOBILE HEADER */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] items-center justify-between border-b border-border bg-background/80 px-3 pt-[env(safe-area-inset-top,0px)] backdrop-blur-lg">
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </Button>

        <span className="font-bold text-sm text-foreground tracking-tight">
          Gryll.ai
        </span>

        <div className="h-11 w-11" aria-hidden="true" />
      </div>

      {/* MOBILE DRAWER */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent
          side="left"
          className="w-72 bg-background border-r border-border p-0 flex flex-col"
        >
          <div className="px-4 pt-5 pb-3 border-b border-border">
            <SheetTitle className="text-base font-bold text-foreground">
              Gryll.ai
            </SheetTitle>
          </div>

          <div className="flex-1 flex flex-col gap-1 px-3 py-3">
            {navItems.map((item) => (
              <NavItem key={item.path} {...item} />
            ))}
          </div>

          <div className="px-3 pb-3 space-y-1 border-t border-border pt-3">
            {/* Mobile Notification Bell */}
            {user && notifications && (
              <div className="py-1">
                <NotificationBell
                  notifications={notifications.notifications}
                  unreadCount={notifications.unreadCount}
                  onMarkRead={notifications.markAsRead}
                  onDelete={notifications.deleteNotification}
                  onClearAll={notifications.clearAll}
                  collapsed={false}
                />
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 h-9 text-xs text-muted-foreground"
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
              {theme === "dark" ? "Dark Mode" : "Light Mode"}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 h-9 text-xs text-muted-foreground"
              onClick={() => {
                navigate("/profile");
                setIsDrawerOpen(false);
              }}
            >
              {renderProfileIcon(16)}
              Profile
            </Button>

            {user ? (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-3 h-9 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                Logout
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-3 h-9 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                onClick={handleLogin}
              >
                <LogIn size={16} />
                Sign In
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* DESKTOP SIDEBAR */}
      <div
        className={`hidden md:flex flex-col h-full bg-[var(--sidebar-background)] border-r border-[var(--sidebar-border)] py-4 z-30 transition-all duration-300 ${collapsed ? "w-[52px]" : "w-[140px]"}`}
      >
        {/* Header Row: Logo (when expanded) + Collapse Toggle */}
        <div
          className={`px-1.5 mb-4 flex items-center ${collapsed ? "justify-center" : "justify-between"}`}
        >
          {/* Brand Logo - Only show when expanded */}
          {!collapsed && (
            <div className="flex items-center gap-2 pl-1.5">
              <img
                src={gryllIcon}
                alt="Gryll Icon"
                className="w-6 h-6 object-contain"
              />
              <span
                className="font-bold text-lg tracking-tight"
                style={{ color: "#5C4523" }}
              >
                gryll.ai
              </span>
            </div>
          )}

          {/* Collapse Toggle */}
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground"
                onClick={() => setCollapsed(!collapsed)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? (
                  <ChevronRight size={16} />
                ) : (
                  <ChevronLeft size={16} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{collapsed ? "Expand" : "Collapse"}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Top Nav */}
        <div
          className={`flex flex-col gap-1 px-1.5 ${collapsed ? "items-center" : ""}`}
        >
          {navItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </div>

        {/* Bottom Actions */}
        <div
          className={`flex flex-col gap-1 px-1.5 mt-auto ${collapsed ? "items-center" : ""}`}
        >
          {/* Notification Bell */}
          {user && notifications && (
            <NotificationBell
              notifications={notifications.notifications}
              unreadCount={notifications.unreadCount}
              onMarkRead={notifications.markAsRead}
              onDelete={notifications.deleteNotification}
              onClearAll={notifications.clearAll}
              collapsed={collapsed}
            />
          )}

          <div className="h-px bg-border my-1" />

          {collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-amber-500"
                  onClick={toggleTheme}
                >
                  {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{theme === "dark" ? "Dark Mode" : "Light Mode"}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 h-9 px-3 text-xs text-muted-foreground hover:text-amber-500"
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
              <span className="whitespace-nowrap">
                {theme === "dark" ? "Dark" : "Light"}
              </span>
            </Button>
          )}

          {collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    location.pathname === "/profile" ? "secondary" : "ghost"
                  }
                  size="icon"
                  className="h-9 w-9 text-muted-foreground"
                  onClick={() => navigate("/profile")}
                >
                  {renderProfileIcon(18)}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Profile</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant={location.pathname === "/profile" ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start gap-3 h-9 px-3 text-xs"
              onClick={() => navigate("/profile")}
            >
              {renderProfileIcon(16)}
              <span className="whitespace-nowrap">Profile</span>
            </Button>
          )}

          {user ? (
            collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-red-500"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-3 h-9 px-3 text-xs text-muted-foreground hover:text-red-500"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span className="whitespace-nowrap">Logout</span>
              </Button>
            )
          ) : collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-emerald-500"
                  onClick={handleLogin}
                >
                  <LogIn size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Sign In</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 h-9 px-3 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={handleLogin}
            >
              <LogIn size={16} />
              <span className="whitespace-nowrap">Sign In</span>
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

Sidebar.propTypes = {
  handleLogin: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  user: PropTypes.shape({
    uid: PropTypes.string,
    email: PropTypes.string,
    displayName: PropTypes.string,
    avatar_url: PropTypes.string,
    avatarUrl: PropTypes.string,
  }),
  notifications: PropTypes.shape({
    notifications: PropTypes.array,
    unreadCount: PropTypes.number,
    markAsRead: PropTypes.func,
    deleteNotification: PropTypes.func,
  }),
};

export default Sidebar;
